import { Component } from 'vue-property-decorator';

import { mixins } from 'vue-class-component';

import ReportMetricsMixin from '@/views/reports/mixins/report-metrics-mixin';
import {METRICS_CUSTOM_COLUMN, METRICS_CUSTOM_EVENT, METRICS_CUSTOM_EVENT_REVENUE, METRICS_CUSTOM_EVENT_HIDDEN_REVENUE} from '@/services/constants/report';
import {isEqual} from 'lodash';

@Component
export default class MetricsMixin extends mixins<ReportMetricsMixin>(ReportMetricsMixin) {
  getInheritColumns(altertative: boolean = false) {
    return this.$store.state.user.independentColumnSettingsEnabled === altertative;
  }

  get storeColumns() {
    switch(this.routeName) {
      case 'app.conversions':
        return 'conversions';
      case 'app.postbacks':
        return 'postbacks';
      case 'app.errors':
        return 'errors';
      case 'app.live-clicks':
        return 'live-clicks';
      case 'app.live-visits':
        return 'live-visits';

      default:
        return this.$route.params.type;
    }
  }

  groupingValues: any[] = [];

  get groupingValuesValueList() {
    return this.groupingValues.map((item: any) => item.value);
  }

  get routeName() {
    return this.$route.name;
  }

  findOrderByIn(value: any[], source: string[]) {
    return source.find(
      (defaultSortItem) => value.find(
        (valueItem) => valueItem === defaultSortItem
      )
    )
  }
  
  dimensionType(dimension:string){
    if(dimension==='conversions'){
      return 'app.conversions'
    }
    if(dimension==='postbacks'){
      return 'app.postbacks';
    }
    if(dimension==='errors'){
      return 'app.errors';
    }
    if(dimension==='live-clicks'){
      return 'app.live-clicks';
    }
    if(dimension==='live-visits'){
      return 'app.live-visits';
    }
    
    return 'app.report';
  }

  findOrderBy(value: any[], dimension = this.$route.params.type) {
    let result = null;

    //try to find orderBy in default_sort
    if(this.metrics.default_sort) {
      let result = this.findOrderByIn(value, this.metrics.default_sort);
      if(result) {
        return result;
      }
    }

    const existingMetrics = this.getExisting_metrics(this.routeName, dimension);

    //try to find orderBy in existing metrics enabled
    const notSortableColumns = this.getCustomMetrics(this.dimensionType(dimension))?.sort_exclude;
    const existingMetricsEnabled = existingMetrics
      .filter((item:any) => !item.disabled && !notSortableColumns.includes(item.value))
      .map((item:any) => item.value);

    result = this.findOrderByIn(value, existingMetricsEnabled);

    if(result) {
      return result;
    }

    //try to find orderBy in existing metrics disabled
    const existingMetricsDisabled = existingMetrics
      .filter((item:any) => item.disabled)
      .map((item:any) => item.value);
    result = this.findOrderByIn(value, existingMetricsDisabled);

    return result;
  }

  getDefaultSelected(type: string = this.$route.params.type) {
    const routeName = this.$route.name;
    const { default_include, default_omit } = this.getMetrics(routeName);

    return this.getExisting_metrics(routeName, type)
      .filter((item: any) => {
        const { value } = item;
        
        if(
          value.startsWith(METRICS_CUSTOM_COLUMN) ||
          value.startsWith(METRICS_CUSTOM_EVENT) ||
          value.startsWith(METRICS_CUSTOM_EVENT_REVENUE) ||
          value.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)
        ) return false;
        
        if (default_include.includes(value)) return true;

        return !default_omit.includes(value);
      });
  }

  get tableFullDimensions() {
    const { dimensions } = this.$store.state.reportColumns

    return {
      ...dimensions,
      'live-visits': {
        additionalColumns: [
          ...this.liveVisitsMetrics.avaliable_metrics
        ],
        mainColumn: '',
      },
      'live-clicks': {
        additionalColumns: [
          ...this.liveClicksMetrics.avaliable_metrics
        ],
        mainColumn: '',
      },
      errors: {
        additionalColumns: [
          ...this.errorsMetrics.avaliable_metrics
        ],
        mainColumn: '',
      },
      postbacks: {
        additionalColumns: [
          ...this.postbacksMetrics.avaliable_metrics
        ],
        mainColumn: '',
      },
      conversions: {
        additionalColumns: [
          ...this.conversionsMetrics.avaliable_metrics
        ],
        mainColumn: '',
      }
    }
  }

  getMetrics(routeName: string) {
    switch (routeName) {
      case 'app.conversions':
        return this.conversionsMetrics;
      case 'app.postbacks':
        return this.postbacksMetrics;
      case 'app.errors':
        return this.errorsMetrics;
      case 'app.live-clicks':
        return this.liveClicksMetrics;
      case 'app.live-visits':
        return this.liveVisitsMetrics;
      default:
        return this.baseMetrics;
    }
  }

  getMetricsTextByValue(value) {
    const text = this.reportMetricsName(value)

    if (text) {
      return text
    }

    const event = Object.values(this.$store.state.table.customEvents)
      .find(({ slug }: any) => `${METRICS_CUSTOM_EVENT_HIDDEN_REVENUE}${slug}` === value || `${METRICS_CUSTOM_EVENT}${slug}` === value || `${METRICS_CUSTOM_EVENT_REVENUE}${slug}` === value) as any

    if (event) {
      return event.name
    }

    return ''
  }

  getShortMetricHelp(name:string) {
    if(name.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || name.startsWith(METRICS_CUSTOM_EVENT)){
      return this.t('Conversion event value specified with “cet“ parameter')
    }
    
    if(name.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)){
      return this.t('Revenue of custom event that was not sent to the traffic source')
    }

    if (name.startsWith(METRICS_CUSTOM_COLUMN)) {
      const formula = this.$store.getters.getCustomColumns[name.slice(METRICS_CUSTOM_COLUMN.length)]?.formula || ''
      return formula.replace(/{(.+?)}/g, (_, value) => this.getMetricsTextByValue(value))
    }

    return this.metrics.metrics_help[name]?.short || null
  }

  getFullMetricHelp(name:string) {
    if(name.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || name.startsWith(METRICS_CUSTOM_EVENT)){
      return this.t('Conversion event value specified with “cet“ parameter. Allows to track additional income from upsells.')
    }
    
    if(name.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)){
      return this.t('Revenue of custom event that was not sent to the traffic source.')
    }

    if (name.startsWith(METRICS_CUSTOM_COLUMN)) {
      const { description, formula = '' } = this.$store.getters.getCustomColumns[name.slice(METRICS_CUSTOM_COLUMN.length)] || {}

      if (description) {
        return description
      }

      return formula.replace(/{(.+?)}/g, (_, value) => this.getMetricsTextByValue(value))
    }

    return this.metrics.metrics_help[name]?.full || null;
  }

  get metrics() {
    return this.getMetrics(this.routeName);
  }

  existingMetricsCustom(routeName = this.routeName) {
    const metrics = this.getMetrics(routeName)
    return metrics.avaliable_metrics.map((name: any, index: number) => {
      let result: any = {
        text: this.reportMetricsName(name),
        value: name,
        index: index,
        help: this.getShortMetricHelp(name),
        helpFull: this.getFullMetricHelp(name),
      };

      if (this.metrics.default_include.includes(name)) {
        result.disabled = true;
        result.selected = true;
      }

      return result;
    });
  }

  get treeLevelColumns() {
    const { dimensions } = this.$store.state.reportColumns
    return this.$store.getters.tableQueryForCurrentTab.treeLevels?.filter((item)=>!!item)?.map((item: string) => {
        const name = dimensions[item].mainColumn;
        return {
          text: this.reportMetricsName(name),
          value: name
        }
      }) || [];
  }

  getExistingMetricsSimple(type: string): any[] {
    const { reportColumns, table } = this.$store.state;
    const { isHideFilters } = table
    const { dimensions, metrics } = reportColumns
    const { additionalColumns = [], mainColumn } = dimensions[type] || {};

    if (!dimensions[type])
      return [];

    let result: any[] = additionalColumns.concat(metrics).map((name: any) => {
      return {
        text: name,
        value: name,
        isCustomColumn: name.startsWith(METRICS_CUSTOM_COLUMN)
      }
    })

    if(!isEqual(this.groupingValues, this.treeLevelColumns)) {
      this.groupingValues = this.treeLevelColumns.map(item => ({ ...item, disabled: true, selected: true, isTreeLevel: true }))
    }

    if(isHideFilters) {
      if(this.metrics.report_exclude[type]) {
        const exclude = this.metrics.report_exclude[type];
        result = result.filter((item:any) => !exclude.includes(item.value));
      }
    }

    const mainColumnValue = [{
      text: mainColumn,
      value: mainColumn,
      disabled: true,
      selected: true
    }];

    return this.applyHelp(mainColumnValue
      .concat(this.groupingValues)
      .concat(result));
  }

  get existing_metrics() {
    const { type } = this.$route.params;
    return this.getExisting_metrics(this.routeName, type);
  }

  getCustomColumnParams(name: string) {
    if (name.startsWith(METRICS_CUSTOM_COLUMN)) {
      const columns = this.$store.getters.getCustomColumns[(name.slice(METRICS_CUSTOM_COLUMN.length))] || {}
      const { id, formula, name: text = '', decimalPlaces, format, description = '' } = columns

      return {
        id,
        formula,
        text,
        decimalPlaces,
        format,
        description,
      }
    }
  }

  applyHelp(value: any[], existedColumns: boolean = true) {
    if(!value) {
      return value;
    }

    return value.map((item, index) => {
      let resultIndex = index;
      if(!existedColumns) {
        const metric = this.existing_metrics.find((existingMetric:any) => existingMetric.value === item.value);
        resultIndex = metric.index;
      }

      return Object.assign(item, {
        help: this.getShortMetricHelp(item.value),
        helpFull: this.getFullMetricHelp(item.value),
        index: resultIndex,
        export: this.groupingValuesValueList.includes(item.value),
        ...this.getCustomColumnParams(item.value),
      })
    });
  }

  getExisting_metrics(routeName: string, type: string) {
    switch (routeName) {
      case 'app.conversions':
      case 'app.postbacks':
      case 'app.errors':
      case 'app.live-visits':
      case 'app.live-clicks':
        return this.existingMetricsCustom(routeName);

      default:
        return this.getExistingMetricsSimple(type);
    }
  }

  get defaultPresetState() {
    return {
      'landings': {
        'width': {
          'landings-name': 300
        },
        'columns': ['landings-name'],
        'order': {
          'orderBy': 'landings-name',
          'orderDir': 'desc'
        },
        'context': 'landings',
        'totalType': {}
      },
      'offers': {
        'width': {
          'offers-name': 300
        },
        'columns': ['offers-name'],
        'order': {
          'orderBy': 'offers-name',
          'orderDir': 'desc'
        },
        'context': 'offers',
        'totalType': {}
      },
      'campaigns': {
        'width': {
          'campaigns-name': 303
        },
        'columns': ['campaigns-name'],
        'order': {
          'orderBy': 'campaigns-name',
          'orderDir': 'desc'
        },
        'context': 'campaigns',
        'totalType': {}
      },
      'custom3': {
        'width': {
          'reports-custom3': 300
        },
        'columns': ['reports-custom3'],
        'order': {
          'orderBy': 'reports-custom3',
          'orderDir': 'desc'
        },
        'context': 'custom3',
        'totalType': {}
      },
      'referrers': {
        'width': {
          'referrers-referrer': 300
        },
        'columns': ['referrers-referrer'],
        'order': {
          'orderBy': 'referrers-referrer',
          'orderDir': 'desc'
        },
        'context': 'referrers',
        'totalType': {}
      },
      'custom7': {
        'width': {
          'reports-custom7': 300
        },
        'columns': ['reports-custom7'],
        'order': {
          'orderBy': 'reports-custom7',
          'orderDir': 'desc'
        },
        'context': 'custom7',
        'totalType': {}
      },
      'mobile-carriers': {
        'width': {
          'reports-mobilecarrier': 300
        },
        'columns': ['reports-mobilecarrier'],
        'order': {
          'orderBy': 'reports-mobilecarrier',
          'orderDir': 'desc'
        },
        'context': 'mobile-carriers',
        'totalType': {}
      },
      'custom6': {
        'width': {
          'reports-custom6': 300
        },
        'columns': ['reports-custom6'],
        'order': {
          'orderBy': 'reports-custom6',
          'orderDir': 'desc'
        },
        'context': 'custom6',
        'totalType': {}
      },
      'day-of-week': {
        'width': {
          'reports-dayofweek': 300
        },
        'columns': ['reports-dayofweek'],
        'order': {
          'orderBy': 'reports-dayofweek',
          'orderDir': 'desc'
        },
        'context': 'day-of-week',
        'totalType': {}
      },
      'states': {
        'width': {
          'reports-state': 300
        },
        'columns': ['reports-state'],
        'order': {
          'orderBy': 'reports-state',
          'orderDir': 'desc'
        },
        'context': 'states',
        'totalType': {}
      },
      'month': {
        'width': {
          'reports-month': 300
        },
        'columns': ['reports-month'],
        'order': {
          'orderBy': 'reports-month',
          'orderDir': 'desc'
        },
        'context': 'month',
        'totalType': {}
      },
      'hour-of-day': {
        'width': {
          'reports-hourofday': 300
        },
        'columns': ['reports-hourofday'],
        'order': {
          'orderBy': 'reports-hourofday',
          'orderDir': 'desc'
        },
        'context': 'hour-of-day',
        'totalType': {}
      },
      'custom9': {
        'width': {
          'reports-custom9': 300
        },
        'columns': ['reports-custom9'],
        'order': {
          'orderBy': 'reports-custom9',
          'orderDir': 'desc'
        },
        'context': 'custom9',
        'totalType': {}
      },
      'custom4': {
        'width': {
          'reports-custom4': 300
        },
        'columns': ['reports-custom4'],
        'order': {
          'orderBy': 'reports-custom4',
          'orderDir': 'desc'
        },
        'context': 'custom4',
        'totalType': {}
      },
      'traffic-sources': {
        'width': {
          'traffic_sources-name': 300
        },
        'columns': ['traffic_sources-name'],
        'order': {
          'orderBy': 'traffic_sources-name',
          'orderDir': 'desc'
        },
        'context': 'traffic-sources',
        'totalType': {}
      },
      'custom10': {
        'width': {
          'reports-custom10': 300
        },
        'columns': ['reports-custom10'],
        'order': {
          'orderBy': 'reports-custom10',
          'orderDir': 'desc'
        },
        'context': 'custom10',
        'totalType': {}
      },
      'custom1': {
        'width': {
          'reports-custom1': 300
        },
        'columns': ['reports-custom1'],
        'order': {
          'orderBy': 'reports-custom1',
          'orderDir': 'desc'
        },
        'context': 'custom1',
        'totalType': {}
      },
      'custom5': {
        'width': {
          'reports-custom5': 300
        },
        'columns': ['reports-custom5'],
        'order': {
          'orderBy': 'reports-custom5',
          'orderDir': 'desc'
        },
        'context': 'custom5',
        'totalType': {}
      },
      'flows': {
        'width': {
          'flows-name': 300
        },
        'columns': ['flows-name'],
        'order': {
          'orderBy': 'flows-name',
          'orderDir': 'desc'
        },
        'context': 'flows',
        'totalType': {}
      },
      'referrer-domains': {
        'width': {
          'referrer_domains-name': 300
        },
        'columns': ['referrer_domains-name'],
        'order': {
          'orderBy': 'referrer_domains-name',
          'orderDir': 'desc'
        },
        'context': 'referrer-domains',
        'totalType': {}
      },
      'cities': {
        'width': {
          'reports-city': 300
        },
        'columns': ['reports-city'],
        'order': {
          'orderBy': 'reports-city',
          'orderDir': 'desc'
        },
        'context': 'cities',
        'totalType': {}
      },
      'isps': {
        'width': {
          'reports-isp': 300
        },
        'columns': ['reports-isp'],
        'order': {
          'orderBy': 'reports-isp',
          'orderDir': 'desc'
        },
        'context': 'isps',
        'totalType': {}
      },
      'custom8': {
        'width': {
          'reports-custom8': 300
        },
        'columns': ['reports-custom8'],
        'order': {
          'orderBy': 'reports-custom8',
          'orderDir': 'desc'
        },
        'context': 'custom8',
        'totalType': {}
      },
      'affiliate-networks': {
        'width': {
          'affiliate_networks-name': 300
        },
        'columns': ['affiliate_networks-name'],
        'order': {
          'orderBy': 'affiliate_networks-name',
          'orderDir': 'desc'
        },
        'context': 'affiliate-networks',
        'totalType': {}
      },
      'custom2': {
        'width': {
          'reports-custom2': 300
        },
        'columns': ['reports-custom2'],
        'order': {
          'orderBy': 'reports-custom2',
          'orderDir': 'desc'
        },
        'context': 'custom2',
        'totalType': {}
      },
      'countries': {
        'width': {
          'reports-country': 300
        },
        'columns': ['reports-country'],
        'order': {
          'orderBy': 'reports-country',
          'orderDir': 'desc'
        },
        'context': 'countries',
        'totalType': {}
      },
      'day': {
        'width': {
          'reports-day': 300
        },
        'columns': ['reports-day'],
        'order': {
          'orderBy': 'reports-day',
          'orderDir': 'desc'
        },
        'context': 'day',
        'totalType': {}
      },
      'conversions': {
        'width': {
          'conversions-postbacktimestamp': 100,
          'conversions-clickid': 250
        },
        'columns': ['conversions-postbacktimestamp','conversions-clickid'],
        'order': {
          'orderBy': 'conversions-clickid',
          'orderDir': 'desc'
        },
        'context': 'conversions',
        'totalType': {}
      },
      'device-vendors': {
        'width': {
          'reports-devicevendor': 300
        },
        'columns': ['reports-devicevendor'],
        'order': {
          'orderBy': 'reports-devicevendor',
          'orderDir': 'desc'
        },
        'context': 'device-vendors',
        'totalType': {}
      },
      'live-visits': {
        'width': {
          'reports-timestamp': 100
        },
        'columns': ['reports-timestamp'],
        'order': {
          'orderBy': 'reports-timestamp',
          'orderDir': 'desc'
        },
        'context': 'live-visits',
        'totalType': {}
      },
      'languages': {
        'width': {
          'reports-language': 300
        },
        'columns': ['reports-language'],
        'order': {
          'orderBy': 'reports-language',
          'orderDir': 'desc'
        },
        'context': 'languages',
        'totalType': {}
      },
      'device-types': {
        'width': {
          'reports-devicetype': 300
        },
        'columns': ['reports-devicetype'],
        'order': {
          'orderBy': 'reports-devicetype',
          'orderDir': 'desc'
        },
        'context': 'device-types',
        'totalType': {}
      },
      'subnet-big': {
        'width': {
          'reports-subnetbig': 300
        },
        'columns': ['reports-subnetbig'],
        'order': {
          'orderBy': 'reports-subnetbig',
          'orderDir': 'desc'
        },
        'context': 'subnet-big',
        'totalType': {}
      },
      'oses': {
        'width': {
          'reports-os': 300
        },
        'columns': ['reports-os'],
        'order': {
          'orderBy': 'reports-os',
          'orderDir': 'desc'
        },
        'context': 'oses',
        'totalType': {}
      },
      'ips': {
        'width': {
          'ips-ip': 300
        },
        'columns': ['ips-ip'],
        'order': {
          'orderBy': 'ips-ip',
          'orderDir': 'desc'
        },
        'context': 'ips',
        'totalType': {}
      },
      'errors': {
        'width': {
          'errors-timestamp': 300
        },
        'columns': ['errors-timestamp'],
        'order': {
          'orderBy': 'errors-timestamp',
          'orderDir': 'desc'
        },
        'context': 'errors',
        'totalType': {}
      },
      'browsers': {
        'width': {
          'reports-browser': 300
        },
        'columns': ['reports-browser'],
        'order': {
          'orderBy': 'reports-browser',
          'orderDir': 'desc'
        },
        'context': 'browsers',
        'totalType': {}
      },
      'browser-versions': {
        'width': {
          'reports-browserversion': 300
        },
        'columns': ['reports-browserversion'],
        'order': {
          'orderBy': 'reports-browserversion',
          'orderDir': 'desc'
        },
        'context': 'browser-versions',
        'totalType': {}
      },
      'connection-types': {
        'width': {
          'reports-connecttype': 300
        },
        'columns': ['reports-connecttype'],
        'order': {
          'orderBy': 'reports-connecttype',
          'orderDir': 'desc'
        },
        'context': 'connection-types',
        'totalType': {}
      },
      'device-models': {
        'width': {
          'reports-devicemodel': 300
        },
        'columns': ['reports-devicemodel'],
        'order': {
          'orderBy': 'reports-devicemodel',
          'orderDir': 'desc'
        },
        'context': 'device-models',
        'totalType': {}
      },
      'screen-resolutions': {
        'width': {
          'reports-screenresolution': 300
        },
        'columns': ['reports-screenresolution'],
        'order': {
          'orderBy': 'reports-screenresolution',
          'orderDir': 'desc'
        },
        'context': 'screen-resolutions',
        'totalType': {}
      },
      'os-versions': {
        'width': {
          'reports-osversion': 300
        },
        'columns': ['reports-osversion'],
        'order': {
          'orderBy': 'reports-osversion',
          'orderDir': 'desc'
        },
        'context': 'os-versions',
        'totalType': {}
      },
      'postbacks': {
        'width': {
          'postbacks-timestamp': 250,
          'postbacks-clickid': 250,
          'postbacks-url': 250,
          'postbacks-status_code': 100,
          'campaigns-name': 100,
          'offers-name': 100,
          'traffic_sources-name': 100
        },
        'columns': ['postbacks-timestamp', 'postbacks-clickid', 'postbacks-url', 'postbacks-status_code', 'campaigns-name', 'offers-name', 'traffic_sources-name'],
        'order': {
          'orderBy': 'postbacks-timestamp',
          'orderDir': 'desc'
        },
        'context': 'postbacks',
        'totalType': {}
      },
      'live-clicks': {
        'width': {
          'reports-timestamp': 100
        },
        'columns': ['reports-timestamp'],
        'order': {
          'orderBy': 'reports-timestamp',
          'orderDir': 'desc'
        },
        'context': 'live-clicks',
        'totalType': {}
      },
      'subnet-small': {
        'width': {
          'reports-subnetsmall': 300
        },
        'columns': ['reports-subnetsmall'],
        'order': {
          'orderBy': 'reports-subnetsmall',
          'orderDir': 'desc'
        },
        'context': 'subnet-small',
        'totalType': {}
      },
      'useragents': {
        'width': {
          'useragents-useragent': 300
        },
        'columns': ['useragents-useragent'],
        'order': {
          'orderBy': 'reports-useragent',
          'orderDir': 'desc'
        },
        'context': 'useragents',
        'totalType': {}
      }
    }
  }
}
